// src/components/Admin/ItemManager/ItemTransactionLog.jsx
import { useEffect, useState, useMemo } from 'react';
import {
    Box, Table, TableHead, TableRow, TableCell,
    TableBody, CircularProgress, Paper, Chip,
    Pagination, Select, MenuItem, Stack, Typography, TextField,
} from '@mui/material';
import api from '../../../api/axios';

const STATUS_LIST = ['전체', '미사용', '사용', '승인대기', '환불됨'];

const displayStatus = (raw) => {
    switch (raw) {
        case '사용완료': return '사용';
        case '승인대기': return '승인대기';
        case '환불됨': return '환불됨';
        case '미사용': return '미사용';
        default: return raw;
    }
};

const statusColor = (st) => ({
    미사용: 'default',
    사용: 'success',
    승인대기: 'warning',
    환불됨: 'info',
}[st.trim()] || 'default');

export default function ItemTransactionLog() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('전체');
    const [searchKeyword, setSearchKeyword] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const rowsPerPage = 10;

    /* ───── 데이터 로드 ───── */
    useEffect(() => {
        api.get('/api/rewards/item-admin/purchase-log/')
            .then((res) => setLogs(res.data.logs || []))
            .catch(() => alert('구매 로그를 불러오는 데 실패했습니다.'))
            .finally(() => setLoading(false));
    }, []);

    /* ───── 필터링 ───── */
    const filteredLogs = useMemo(() => (
        logs
            .filter((l) => {
                const st = displayStatus(l.status || '').trim();
                return statusFilter === '전체' || st === statusFilter;
            })
            .filter((l) => {
                const keyword = searchKeyword.toLowerCase();
                const keywordMatch =
                    l.username.toLowerCase().includes(keyword)
                    || l.item_name.toLowerCase().includes(keyword);
                const dateMatch = selectedDate
                    ? l.purchased_at.startsWith(selectedDate)
                    : true;
                return keywordMatch && dateMatch;
            })
    ), [logs, statusFilter, searchKeyword, selectedDate]);

    /* ───── 페이지네이션 ───── */
    const totalPages = Math.ceil(filteredLogs.length / rowsPerPage) || 1;
    const paginated = filteredLogs.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage,
    );
    const handlePageChange = (_, v) => setPage(v);

    if (loading) return <CircularProgress sx={{ mt: 4 }} />;

    return (
        <Box
            sx={{
                /* 모바일: 전체 90 % 축소 */
                '@media (max-width:600px)': {
                    transform: 'scale(0.9)',
                    transformOrigin: 'top left',
                },
            }}
        >
            {/* 검색 · 필터 바 */}
            <Stack
                direction="row"
                spacing={2}
                sx={{ mb: 2 }}
                flexWrap="wrap"
                alignItems="center"
            >
                <TextField
                    size="small"
                    label="검색 (닉네임 / 아이템명)"
                    value={searchKeyword}
                    onChange={(e) => { setSearchKeyword(e.target.value); setPage(1); }}
                />

                <Select
                    size="small"
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                    sx={{ minWidth: 120 }}
                >
                    {STATUS_LIST.map((l) => (
                        <MenuItem key={l} value={l}>{l}</MenuItem>
                    ))}
                </Select>

                <TextField
                    type="date"
                    size="small"
                    label="날짜"
                    InputLabelProps={{ shrink: true }}
                    value={selectedDate}
                    onChange={(e) => { setSelectedDate(e.target.value); setPage(1); }}
                />

                <Typography variant="body2" sx={{ ml: 'auto', pr: 1 }}>
                    총 {filteredLogs.length}건
                </Typography>
            </Stack>

            {/* 테이블 */}
            <Paper
                sx={{
                    /* 모바일 가로 스크롤 허용 */
                    '@media (max-width:600px)': {
                        width: 'max-content',
                        overflowX: 'auto',
                    },
                }}
            >
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>아이템명</TableCell>
                            <TableCell>구매자</TableCell>
                            <TableCell>가격</TableCell>
                            <TableCell>구매일시</TableCell>
                            <TableCell>상태</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paginated.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                    🔍 조건에 맞는 로그가 없습니다.
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginated.map((log) => (
                                <TableRow key={log.id}>
                                    <TableCell>{log.item_name}</TableCell>
                                    <TableCell>{log.username}</TableCell>
                                    <TableCell>{log.price} Leaf🌿</TableCell>
                                    <TableCell>{log.purchased_at}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={displayStatus(log.status)}
                                            color={statusColor(displayStatus(log.status))}
                                            size="small"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Paper>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
                <Box mt={2} display="flex" justifyContent="center">
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                        sx={{ '@media (max-width:600px)': { transform: 'scale(0.9)' } }}
                    />
                </Box>
            )}
        </Box>
    );
}
